import React, { useEffect } from 'react'
import { Routes, Route, Outlet } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Toaster } from 'react-hot-toast'

import Authorize, { AuthorizeFeature } from './Authorize'

import Navbar from './Navbar'
import Dashboard from './Dashboard'
import FlowContainer from './Flow'
import FlowLoader from './FlowLoader'
import UsersComponent from './Users'
import AnswersList from './answers/AnswersList'
import EditAnswers from './EditAnswers'
import GuidesList from './guides/GuidesList'
import EditGuides from './EditGuides'
import KnowledgeBaseList from './knowledge-base/KnowledgeBaseList'
import KnowledgeBaseImport from './knowledge-base/KnowledgeBaseImport'
import Training from './Training'
import NewCampaign from './campaigns/EditCampaign'
import CampaignsList from './campaigns/CampaignsList'
import ErrorPage from './ErrorPage'

import { fetchBasics, setCsrfToken } from 'Actions/app'
import { getAppState } from 'Selectors/app'

import { useGetCsrfTokenQuery } from 'Services/me'

import { APP_STATES } from '../constants'

const AuthorizedUsersRoute = Authorize(UsersComponent, ['user_read'])
const AuthorizedKBList = AuthorizeFeature(KnowledgeBaseList, 'knowledgeBase', true)
const AuthorizedKBImport = AuthorizeFeature(KnowledgeBaseImport, 'knowledgeBase', true)
const AuthorizedTraining = AuthorizeFeature(Training, 'training', true)
const AuthorizedCampaignsList = AuthorizeFeature(CampaignsList, 'campaign')
const AuthorizedNewCampaign = AuthorizeFeature(NewCampaign, 'campaign')

const NotFound = () => {
  return (
    <div style={{ display: 'grid', height: '80vh' }}>
      <span style={{ margin: 'auto' }}>Page not found.</span>
    </div>
  )
}

const MainContainer = () => {
  return (
    <div className='main-container'>
      <Outlet />
    </div>
  )
}

const ProtectedRoutes = () => {
  const dispatch = useDispatch()

  const csrfData = useGetCsrfTokenQuery()

  const appState = useSelector(getAppState)

  useEffect(() => {
    if (csrfData.isSuccess && csrfData.status === 'fulfilled') {
      const { data } = csrfData
      dispatch(setCsrfToken({ token: data.csrf_token }))
      dispatch(fetchBasics())
    }
  }, [csrfData, csrfData.isSuccess, dispatch])

  if (appState === APP_STATES.LOADING && (csrfData.isLoading || csrfData.isFetching)) {
    return (
      <div className='home-loader loader-container'>
        <div className='spinner' />
      </div>
    )
  }

  if (appState === APP_STATES.ERROR) {
    return <ErrorPage />
  }

  return (
    <React.Fragment>
      <Navbar />
      <div className='home-container'>
        <Routes>
          <Route path='bots/:botId' element={<Outlet />}>
            <Route index element={<FlowLoader />} />
            <Route path='flows/:flowId' element={<FlowContainer />} />
          </Route>
          <Route path='answers/create' element={<EditAnswers />} />
          <Route path='answers/edit/:id' element={<EditAnswers isEditMode />} />
          <Route path='guides/create' element={<EditGuides />} />
          <Route path='guides/edit/:id' element={<EditGuides isEditMode />} />
          <Route path='campaigns/create' element={<AuthorizedNewCampaign />} />
          <Route path='campaigns/edit/:id' element={<AuthorizedNewCampaign />} />
          <Route path='*' element={<MainContainer />}>
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='users' element={<AuthorizedUsersRoute />} />
            <Route path='answers' element={<AnswersList />} />
            <Route path='guides' element={<GuidesList />} />
            <Route path='campaigns' element={<AuthorizedCampaignsList />} />
            <Route path='knowledge-base' element={<AuthorizedKBList />} />
            <Route path='knowledge-base-import' element={<AuthorizedKBImport />} />
            <Route path='training' element={<AuthorizedTraining />} />
            <Route path='*' element={<NotFound />} />
          </Route>
        </Routes>
      </div>
      <Toaster toastOptions={{ style: { zIndex: 200 } }} />
    </React.Fragment>
  )
}

export default ProtectedRoutes
